import React, { useState, useCallback, useEffect, useMemo } from "react";
import { type NodeProps, type Node, useReactFlow } from "@xyflow/react";
import {
  ClipboardCopy,
  Plus,
  ArrowRight,
  Code,
  Trash2,
  X,
} from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { WorkflowBaseNode } from "../../common/shared/WorkflowBaseNode";
import { WorkflowNodeModal } from "../../common/shared/WorkflowNodeModal";
import { AvailableVariable } from "../../common/ui/types";

interface Mapping {
  id: number;
  source: string;
  target: string;
}

export const TransformNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  // ── Mapping state ─────────────────────────────────────────────────
  const [mappings, setMappings] = useState<Mapping[]>(() => {
    const initial = data?.mappings || [{ id: 1, source: "", target: "" }];
    return initial.map((m: any, idx: number) => ({
      ...m,
      id: m.id || idx + 1,
    }));
  });

  const [providedData, setProvidedData] = useState<string>(
    data?.provided_data || ""
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setNodes, getNodes, getEdges } = useReactFlow();

  const [activeMappingId, setActiveMappingId] = useState<number | null>(null);
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

  // ── Validation ────────────────────────────────────────────────────
  const validationStatus = useMemo(() => {
    const hasValidMapping = mappings.some((m) => m.source && m.target);
    return hasValidMapping
      ? { isValid: true, errors: [] as string[] }
      : {
          isValid: false,
          errors: ["At least one source and target mapping is required."],
        };
  }, [mappings]);

  // ── Sync config to ReactFlow ──────────────────────────────────────
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                mappings,
                provided_data: providedData,
              },
            }
          : node
      )
    );
  }, [mappings, providedData, id, setNodes]);

  // ── Mapping handlers ──────────────────────────────────────────────
  const handleMappingChange = (
    mappingId: number,
    field: keyof Mapping,
    value: string
  ) => {
    setMappings((prev) =>
      prev.map((m) => (m.id === mappingId ? { ...m, [field]: value } : m))
    );
  };

  const handleAddMapping = () => {
    const newId =
      mappings.length > 0 ? Math.max(...mappings.map((m) => m.id)) + 1 : 1;
    const newMapping: Mapping = { id: newId, source: "", target: "" };
    setMappings((prev) => [...prev, newMapping]);
  };

  const handleRemoveMapping = (mappingId: number) => {
    if (mappings.length === 1) {
      setMappings([{ id: 1, source: "", target: "" }]);
      return;
    }
    setMappings((prev) => prev.filter((m) => m.id !== mappingId));
  };

  // ── Extract node data helper ──────────────────────────────────────
  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }
    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs" && key !== "executionStatus") inputs[key] = value;
    });
    return { inputs, outputs };
  }, []);

  // ── Previous nodes (for custom left sidebar) ─────────────────────
  const previousNodes = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const availableVariables: AvailableVariable[] = [];

    const incomingEdges = edges.filter((edge) => edge.target === id);
    incomingEdges.forEach((edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      if (sourceNode) {
        const { inputs, outputs } = extractNodeData(sourceNode.data);
        availableVariables.push({
          nodeId: sourceNode.id,
          nodeName: sourceNode.data?.name || sourceNode.id,
          nodeType: sourceNode.type || "unknown",
          inputs,
          outputs,
        });
      }
    });

    return availableVariables;
  }, [id, getNodes, getEdges, isModalOpen, extractNodeData]);

  // ── Auto-set providedData from first previous node ────────────────
  useEffect(() => {
    if (previousNodes.length > 0) {
      const nodeName = previousNodes[0].nodeName;
      const basePrefix = `${nodeName}.outputs`;
      if (!providedData || !providedData.startsWith(nodeName)) {
        setProvidedData(basePrefix);
      }
    }
  }, [previousNodes, providedData]);

  // ── Drag & Drop handlers ──────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent, variable: string) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", variable);
  };

  const handleDrop = (e: React.DragEvent, mappingId: number) => {
    e.preventDefault();
    e.stopPropagation();
    let variable = e.dataTransfer.getData("text/plain");

    if (variable && providedData) {
      const nodeParts = providedData.split(".");
      if (nodeParts.length >= 2) {
        const nodeRoot = `${nodeParts[0]}.${nodeParts[1]}`;
        if (variable.startsWith(nodeRoot + ".")) {
          variable = variable.replace(nodeRoot + ".", "");
        }
      }

      const providedParts = providedData.split(".");
      const variableParts = variable.split(".");
      if (providedParts.length > 0 && variableParts.length > 0) {
        const lastProvidedSegment = providedParts[providedParts.length - 1];
        if (variableParts[0] === lastProvidedSegment) {
          variable = variableParts.slice(1).join(".");
        }
      }
    }

    if (variable) {
      setMappings((prev) =>
        prev.map((m) => {
          if (m.id === mappingId) {
            const hasWildcard = variable.includes("[*]");
            let newTarget = m.target;
            if (!newTarget) {
              const parts = variable.split(".");
              const leaf = parts[parts.length - 1]
                .replace(/\[\*\]/g, "")
                .replace(/\[\d+\]/g, "");
              newTarget = hasWildcard ? `[*].${leaf}` : leaf;
            }
            return { ...m, source: variable, target: newTarget };
          }
          return m;
        })
      );
    }
    setActiveMappingId(null);
  };

  const handleDragOver = (e: React.DragEvent, mappingId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMappingId(mappingId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveMappingId(null);
  };

  // ── Collapse/expand for nested keys ───────────────────────────────
  const toggleCollapse = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const formatPrimitiveValue = (value: any): string => {
    if (value === null || value === undefined) return "null";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "string")
      return `"${value.length > 30 ? value.slice(0, 30) + "..." : value}"`;
    return String(value);
  };

  // ── Recursive nested keys renderer (custom left sidebar) ─────────
  const renderNestedKeys = useCallback(
    (
      data: any,
      path: string,
      depth: number = 0,
      wildcardPath?: string
    ): React.ReactNode => {
      if (data === null || data === undefined) return null;

      const currentDragPath = wildcardPath || path;

      if (Array.isArray(data)) {
        const arrayWildcardPath = currentDragPath
          ? `${currentDragPath.replace(/\[\d+\]/g, "[*]")}[*]`
          : `[*]`;

        return (
          <div className="space-y-1">
            <div className="ml-2">
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, arrayWildcardPath)}
                className="flex items-center gap-1 border border-purple-200 rounded-md px-2 py-1 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 cursor-move transition-all text-[10px] group"
                title="Map all items in this array"
              >
                <span className="font-bold text-purple-700">[*]</span>
                <span className="text-purple-500 ml-1 font-medium italic group-hover:not-italic">
                  Map entire array structure
                </span>
              </div>
            </div>

            {data.length > 0 &&
              data.slice(0, 5).map((item, index) => {
                const itemPath = path ? `${path}[${index}]` : `[${index}]`;
                const itemSpecificPath = currentDragPath
                  ? `${currentDragPath}[${index}]`
                  : `[${index}]`;
                const isObject = item !== null && typeof item === "object";
                const isCollapsed = collapsedKeys.has(itemPath);

                return (
                  <div key={index} className="ml-2">
                    <div
                      draggable
                      onDragStart={(e) =>
                        handleDragStart(e, itemSpecificPath)
                      }
                      className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-1 bg-gray-50 hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-100 hover:border-purple-300 cursor-move transition-all text-[10px]"
                    >
                      {isObject && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            toggleCollapse(itemPath);
                          }}
                          className="text-gray-500 hover:text-purple-600 p-0.5"
                          draggable={false}
                        >
                          {isCollapsed ? "\u25B6" : "\u25BC"}
                        </button>
                      )}
                      <span className="font-semibold text-blue-700">
                        [{index}]
                      </span>
                      {!isObject && (
                        <span className="text-gray-500 ml-1 truncate">
                          {formatPrimitiveValue(item)}
                        </span>
                      )}
                    </div>
                    {isObject && !isCollapsed && (
                      <div className="ml-3 mt-1 border-l-2 border-purple-200 pl-2">
                        {renderNestedKeys(
                          item,
                          itemPath,
                          depth + 1,
                          itemSpecificPath
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            {data.length > 5 && (
              <div className="ml-2 text-[9px] text-gray-400 italic">
                ... and {data.length - 5} more items
              </div>
            )}
          </div>
        );
      }

      if (typeof data === "object") {
        const entries = Object.entries(data);
        return (
          <div className="space-y-1">
            {entries.map(([key, value]) => {
              const keyPath = path ? `${path}.${key}` : key;
              const itemSpecificPath = currentDragPath
                ? `${currentDragPath}.${key}`
                : key;
              const isExpandable =
                value !== null && typeof value === "object";
              const isCollapsed = collapsedKeys.has(keyPath);
              const isArray = Array.isArray(value);

              return (
                <div key={key}>
                  <div className="flex items-center gap-1 border border-gray-200 rounded-md px-2 py-1 bg-gray-50 hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-100 hover:border-purple-300 transition-all group/item">
                    {isExpandable && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleCollapse(keyPath);
                        }}
                        className="text-gray-500 hover:text-purple-600 p-0.5 text-[10px]"
                      >
                        {isCollapsed ? "\u25B6" : "\u25BC"}
                      </button>
                    )}

                    <div
                      draggable
                      onDragStart={(e) =>
                        handleDragStart(e, itemSpecificPath)
                      }
                      className="text-[11px] font-semibold text-gray-900 truncate cursor-move hover:text-purple-700 transition-colors"
                      title={`Drag to map specific item: ${itemSpecificPath}`}
                    >
                      {key}
                    </div>

                    {currentDragPath &&
                      currentDragPath.match(/\[\d+\]/) && (
                        <div
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(
                              e,
                              itemSpecificPath.replace(/\[\d+\]/g, "[*]")
                            )
                          }
                          className="text-[8px] font-bold bg-purple-100 text-purple-600 px-1 py-0.5 rounded cursor-move hover:bg-purple-600 hover:text-white transition-all shadow-sm"
                          title="Drag to map for ALL items in this array"
                        >
                          [*]
                        </div>
                      )}

                    {isArray && (
                      <span className="text-[9px] text-purple-600 bg-purple-100 px-1 rounded ml-auto">
                        [{(value as any[]).length}]
                      </span>
                    )}
                    {!isExpandable && (
                      <span className="text-[9px] text-gray-500 ml-1 truncate max-w-[80px]">
                        {formatPrimitiveValue(value)}
                      </span>
                    )}
                  </div>
                  {isExpandable && !isCollapsed && (
                    <div className="ml-3 mt-1 border-l-2 border-purple-200 pl-2">
                      {renderNestedKeys(
                        value,
                        keyPath,
                        depth + 1,
                        itemSpecificPath
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      return null;
    },
    [collapsedKeys, toggleCollapse, handleDragStart, formatPrimitiveValue]
  );

  // ── Output JSON preview ───────────────────────────────────────────
  const outputJson = useMemo(() => {
    const generateNestedObject = (mappings: Mapping[]) => {
      const result: any = {};
      const validMappings = mappings.filter((m) => m.source && m.target);

      for (const mapping of validMappings) {
        const keys = mapping.target.split(".");
        let current = result;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== "object") {
            if (key.endsWith("[*]")) {
              const cleanKey = key.replace("[*]", "");
              if (!current[cleanKey] || !Array.isArray(current[cleanKey])) {
                current[cleanKey] = [{}];
              }
              current = current[cleanKey][0];
              continue;
            }
            current[key] = {};
          }
          current = current[key];
        }

        const lastKey = keys[keys.length - 1];
        if (lastKey.endsWith("[*]")) {
          const cleanKey = lastKey.replace("[*]", "");
          current[cleanKey] = [`{{${mapping.source}}}`];
        } else {
          current[lastKey] = `{{${mapping.source}}}`;
        }
      }
      return result;
    };

    const output = generateNestedObject(mappings);
    return JSON.stringify(output, null, 2);
  }, [mappings]);

  const highlightedJson = useMemo(() => {
    return syntaxHighlight(outputJson);
  }, [outputJson]);

  // ── Actual execution outputs ──────────────────────────────────────
  const hasExecutionOutputs =
    data?.outputs && Object.keys(data.outputs).length > 0;

  const actualOutputsJson = useMemo(() => {
    if (hasExecutionOutputs) {
      return JSON.stringify(data.outputs, null, 2);
    }
    return "";
  }, [data?.outputs, hasExecutionOutputs]);

  const highlightedActualOutputs = useMemo(() => {
    if (actualOutputsJson) {
      return syntaxHighlight(actualOutputsJson);
    }
    return "";
  }, [actualOutputsJson]);

  // ── Inline content: mapping count summary ─────────────────────────
  const validMappingCount = mappings.filter(
    (m) => m.source && m.target
  ).length;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Transform"
      icon={<ClipboardCopy className="w-4 h-4 text-purple-600" />}
      nodeDocsType="TRANSFORM"
      themeColor="purple"
      configureLabel="Configure Mappings"
      isConfigured={validationStatus.isValid}
      hasSourceHandle={true}
      validationErrors={validationStatus.errors}
      statusText={{
        running: "Executing...",
        success: "Execution Success",
        error: "Execution Error",
      }}
      onConfigure={() => setIsModalOpen(true)}
    >
      {/* Inline preview: mapping count summary */}
      <div className="space-y-2">
        <div className="bg-gray-50 rounded-lg p-2 space-y-1.5 border border-gray-100 text-[10px]">
          <div className="flex items-center gap-1.5 text-gray-500">
            <ClipboardCopy className="w-3 h-3" />
            <span className="font-medium">
              {validMappingCount} mapping{validMappingCount !== 1 ? "s" : ""}{" "}
              configured
            </span>
          </div>
          {validMappingCount > 0 && (
            <div className="bg-white border px-2 py-1 rounded text-gray-700 truncate">
              {mappings
                .filter((m) => m.source && m.target)
                .slice(0, 3)
                .map((m) => `${m.source} \u2192 ${m.target}`)
                .join(", ")}
              {validMappingCount > 3 && ` +${validMappingCount - 3} more`}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        nodeId={id}
        title="Transform - Data mapping"
        icon={<ClipboardCopy className="w-5 h-5 text-purple-600" />}
        themeColor="purple"
        nodeDocsType="TRANSFORM"
        validationStatus={validationStatus}
        saveLabel="Save Mappings"
        onSave={() => setIsModalOpen(false)}
        nodeData={data}
        nodeIcon={<ClipboardCopy className="w-6 h-6 text-gray-400" />}
        emptyOutputsText="No outputs yet"
        renderLeftSidebar={() => (
          <div className="w-[420px] min-w-[420px] bg-gradient-to-b from-gray-50 to-gray-100 overflow-y-auto custom-scrollbar p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                Available Inputs
              </h3>
              <span className="text-[10px] font-medium text-gray-500 bg-white px-1.5 py-0.5 rounded-full">
                {previousNodes.length}
              </span>
            </div>
            {previousNodes.length === 0 ? (
              <div className="p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ClipboardCopy className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs font-medium text-gray-600">
                  No connected nodes
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  Connect nodes to see inputs
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {previousNodes.map((nodeData) => {
                  const hasOutputs =
                    Object.keys(nodeData.outputs).length > 0;
                  const hasInputs =
                    Object.keys(nodeData.inputs).length > 0;
                  const hasData = hasOutputs || hasInputs;

                  if (!hasData) return null;

                  return (
                    <div
                      key={nodeData.nodeId}
                      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                    >
                      <div className="px-3 py-2 bg-gradient-to-r from-purple-50 to-white border-b border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-900 truncate">
                            {nodeData.nodeName}
                          </span>
                          {hasOutputs && (
                            <span className="text-[9px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">
                              {Object.keys(nodeData.outputs).length}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="p-3 space-y-2 bg-white max-h-[500px] overflow-y-auto custom-scrollbar">
                        {hasOutputs && (
                          <div>
                            <h5 className="text-[9px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                              Outputs
                            </h5>
                            {renderNestedKeys(nodeData.outputs, "")}
                          </div>
                        )}
                        {hasInputs && (
                          <div className="mt-2">
                            <h5 className="text-[9px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                              Inputs
                            </h5>
                            {renderNestedKeys(nodeData.inputs, "")}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        renderOutputsSidebar={() => (
          <div className="w-[380px] min-w-[380px] bg-white overflow-y-auto custom-scrollbar p-4 flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-purple-600" />
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                  {hasExecutionOutputs ? "Execution Output" : "JSON Template"}
                </h3>
              </div>
              <span
                className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  hasExecutionOutputs
                    ? "text-green-700 bg-green-100"
                    : "text-gray-500 bg-gray-100"
                }`}
              >
                {hasExecutionOutputs
                  ? `${Object.keys(data.outputs).length} fields`
                  : `${mappings.filter((m) => m.target).length} fields`}
              </span>
            </div>

            {hasExecutionOutputs ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                <pre
                  className="whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{
                    __html: highlightedActualOutputs,
                  }}
                />
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                <pre
                  className="whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: highlightedJson }}
                />
              </div>
            )}

            {!hasExecutionOutputs &&
              mappings.filter((m) => m.target).length === 0 && (
                <div className="mt-4 p-4 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Code className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-xs font-medium text-gray-600">
                    No output yet
                  </p>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Define target fields to see JSON
                  </p>
                </div>
              )}
          </div>
        )}
      >
        {/* ── Center content: Pro Tips + Field Mappings ─────────── */}
        <div className="flex flex-col h-full overflow-hidden">
          {/* Syntax Help / Pro Tips */}
          <div className="shrink-0 bg-purple-50 px-5 py-2 border-b border-purple-100 flex items-center gap-6 overflow-x-auto">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-[10px] font-bold text-purple-700 uppercase">
                Pro Tips:
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px]">
              <div className="flex items-center gap-1.5 border-r border-purple-200 pr-4">
                <code className="bg-white border border-purple-200 px-1 rounded text-purple-700 font-bold">
                  [*]
                </code>
                <span className="text-gray-600">
                  Use wildcards to map arrays (e.g.,{" "}
                  <code className="text-xs">users[*].id</code>)
                </span>
              </div>
              <div className="flex items-center gap-1.5 border-r border-purple-200 pr-4">
                <code className="bg-white border border-purple-200 px-1 rounded text-purple-700 font-bold">
                  .
                </code>
                <span className="text-gray-600">
                  Use dots for nested objects (e.g.,{" "}
                  <code className="text-xs">profile.name</code>)
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <code className="bg-white border border-purple-200 px-1 rounded text-purple-700 font-bold">
                  [0]
                </code>
                <span className="text-gray-600">
                  Access specific items by index
                </span>
              </div>
            </div>
          </div>

          {/* Field Mappings */}
          <div className="flex-1 overflow-y-auto custom-scrollbar bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Field Mappings
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Drag fields from left or type manually
                </p>
              </div>
              <button
                onClick={handleAddMapping}
                className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-semibold rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all flex items-center gap-1.5 shadow-md hover:shadow-lg"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Mapping
              </button>
            </div>

            <div className="space-y-2">
              {mappings.map((mapping) => (
                <div
                  key={mapping.id}
                  className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-xl bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:border-purple-100 hover:shadow-md transition-all group/row"
                >
                  <div className="flex-1">
                    <label className="block text-[9px] font-bold text-gray-500 mb-1.5 uppercase tracking-wider">
                      Source Field
                    </label>
                    <div
                      onDrop={(e) => handleDrop(e, mapping.id)}
                      onDragOver={(e) => handleDragOver(e, mapping.id)}
                      onDragLeave={handleDragLeave}
                      className={`min-h-[42px] px-3 py-2 border-2 rounded-lg text-xs transition-all flex items-center ${
                        activeMappingId === mapping.id
                          ? "border-purple-500 border-dashed bg-purple-50 shadow-md scale-[1.01]"
                          : mapping.source
                            ? "border-emerald-400 border-dashed bg-emerald-50"
                            : "border-gray-200 border-solid bg-white hover:border-purple-200"
                      }`}
                    >
                      {mapping.source ? (
                        <div className="flex items-center justify-between w-full gap-2">
                          <code className="text-[11px] text-emerald-800 font-mono font-bold break-all">
                            {mapping.source}
                          </code>
                          <button
                            onClick={() =>
                              handleMappingChange(mapping.id, "source", "")
                            }
                            className="text-gray-400 hover:text-red-500 p-1 hover:bg-white rounded-md transition-all shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-[11px] italic">
                          Drop or type variable
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-5 shrink-0">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100 shadow-sm">
                      <ArrowRight className="w-4 h-4 text-purple-600" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <label className="block text-[8px] font-bold text-gray-400 mb-1 uppercase tracking-[0.1em] flex items-center justify-between">
                      <span>Target Field</span>
                      {mapping.source.includes("[*]") &&
                        !mapping.target.includes("[*]") && (
                          <button
                            onClick={() =>
                              handleMappingChange(
                                mapping.id,
                                "target",
                                `[*].${mapping.target}`
                              )
                            }
                            className="text-[7px] bg-purple-50 text-purple-600 px-1 rounded hover:bg-purple-100 transition-colors border border-purple-200"
                            title="Add array mapping prefix"
                          >
                            Use [*]
                          </button>
                        )}
                    </label>
                    <div className="relative flex items-center">
                      {mapping.target.startsWith("[*].") && (
                        <div className="absolute left-2 flex items-center gap-0.5">
                          <button
                            onClick={() =>
                              handleMappingChange(
                                mapping.id,
                                "target",
                                mapping.target.replace("[*].", "")
                              )
                            }
                            className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1 py-0.5 rounded border border-purple-200 hover:bg-purple-100 hover:border-purple-400 transition-all flex items-center gap-1 group/badge shadow-sm"
                            title="Remove array mapping (convert to straight transform)"
                          >
                            [*]
                            <X className="w-2 h-2 text-purple-300 group-hover/badge:text-purple-600" />
                          </button>
                          <span className="text-purple-300 text-[10px] font-bold">
                            .
                          </span>
                        </div>
                      )}
                      <input
                        type="text"
                        value={
                          mapping.target.startsWith("[*].")
                            ? mapping.target.replace("[*].", "")
                            : mapping.target
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          const fullVal = mapping.target.startsWith("[*].")
                            ? `[*].${val}`
                            : val;
                          handleMappingChange(mapping.id, "target", fullVal);
                        }}
                        placeholder="Variable name..."
                        className={`w-full py-2 bg-gray-50/50 border border-gray-200 rounded-lg text-[11px] font-medium text-gray-900 focus:bg-white focus:border-purple-400 focus:ring-4 focus:ring-purple-50 outline-none transition-all placeholder:text-gray-300 ${
                          mapping.target.startsWith("[*].")
                            ? "pl-14 pr-3"
                            : "px-3"
                        }`}
                      />
                    </div>
                  </div>

                  <div className="pt-5 shrink-0">
                    <button
                      onClick={() => handleRemoveMapping(mapping.id)}
                      className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-all"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </WorkflowNodeModal>
    </WorkflowBaseNode>
  );
};

export default TransformNode;
